/**
 * Jotai atoms for discounts feature state management
 * Manages pagination, selection, and filter state
 */

import { atom } from 'jotai';

import type { Selection } from 'react-aria-components';
import type { SearchDiscountsParams } from '../api/useSearchDiscounts';

import atomWithDebounce from '@common/atoms/atomWithDebounce';
import { DiscountTab } from '@config/paths/discountTabs';

// Search atoms (debounced)
const searchDebounceAtoms = atomWithDebounce('', 500);
export const debouncedSearchValueAtom = searchDebounceAtoms.debouncedValueAtom;
export const searchValueAtom = searchDebounceAtoms.currentValueAtom;

// Tab filter atom
export const discountTabFilterAtom = atom<DiscountTab | null>(null);

// Pagination atoms
export const currentPageAtom = atom<number>(1);
export const itemsPerPageAtom = atom<number>(10);

// Selection atoms
export const selectedDiscountsAtom = atom<Selection>(new Set<string>());
export const isAllMatchingDiscountsSelectedAtom = atom<boolean>(false);

// Derived atom for API parameters
export const discountsParamsAtom = atom((get): SearchDiscountsParams => {
	const searchValue = get(debouncedSearchValueAtom);
	const discountTab = get(discountTabFilterAtom);
	const currentPage = get(currentPageAtom);
	const itemsPerPage = get(itemsPerPageAtom);

	const params: SearchDiscountsParams = {
		limit: itemsPerPage,
		page: currentPage - 1, // Convert to 0-indexed for API
	};

	if (searchValue.trim()) {
		params.query = searchValue.trim();
	}

	// Build filters object
	const filters: SearchDiscountsParams['filters'] = {};

	// Add tab filter (maps to status)
	if (discountTab && discountTab !== DiscountTab.ALL) {
		filters.tabs = [discountTab];
	}

	if (Object.keys(filters).length > 0) {
		params.filters = filters;
	}

	return params;
});

// Action atom to handle search value changes
export const updateSearchValueAtom = atom(
	(get) => get(searchValueAtom),
	(get, set, newValue: string) => {
		set(debouncedSearchValueAtom, newValue);
		set(currentPageAtom, 1);
	},
);

// Action atom to handle tab filter changes
export const updateDiscountTabFilterAtom = atom(
	(get) => get(discountTabFilterAtom),
	(get, set, newTab: DiscountTab | null) => {
		set(discountTabFilterAtom, newTab);
		set(currentPageAtom, 1);
	},
);

// Update atoms for selection — resets "all matching" when selection changes away from 'all'
export const updateSelectedDiscountsAtom = atom(null, (get, set, newSelection: Selection) => {
	set(selectedDiscountsAtom, newSelection);
	if (newSelection !== 'all') {
		set(isAllMatchingDiscountsSelectedAtom, false);
	}
});

// Update atom for items per page
export const updateItemsPerPageAtom = atom(null, (get, set, newItemsPerPage: number) => {
	set(itemsPerPageAtom, newItemsPerPage);
	set(currentPageAtom, 1); // Reset to first page when changing items per page
});
